/**
 * Abruf-Helfer „Produkt-Medien" (Spec .claude/plans/2026-10-08-produkt-medien.md § 5). Pfade und
 * Felder laut node-app/src/routes/lpFunnel/produktMedien.ts; Typen spiegeln
 * services/lpFunnel/produktMedien.ts + produktMedienStatus.ts (kein geteilter Code zwischen den Apps).
 *
 * Anders als `fetchData` reichen diese Helfer den HTTP-Status durch: 409 (jemand anderes war
 * schneller → neu laden) und 503 (Migration fehlt) verlangen eine andere Reaktion als ein 400.
 */
import { api } from '../../../../helpers/apiUtils';

export const PRODUKT_SLOTS = ['ausgabe', 'quickstart', 'bonus', 'musterdepot', 'autor'] as const;
export type ProduktSlot = (typeof PRODUKT_SLOTS)[number];

export const PRODUKT_SLOT_LABEL: Record<ProduktSlot, string> = {
  ausgabe: 'Ausgabe',
  quickstart: 'Quickstart-Guide',
  bonus: 'Spezialreport / Bonus',
  musterdepot: 'Musterdepot',
  autor: 'Autorenfoto',
};

export const PRODUKT_VARIANTEN = ['einzel', 'flach', 'faecher'] as const;
export type ProduktVariante = (typeof PRODUKT_VARIANTEN)[number];

export const PRODUKT_VARIANTE_LABEL: Record<ProduktVariante, string> = {
  // „einzel“ = ein Exemplar: aus PDF leicht gedreht mit Schatten, aus Bild das verkleinerte Bild
  // (node-app/src/services/lpFunnel/produktMedien.ts PRODUKT_VARIANTEN).
  einzel: 'Einzelexemplar',
  flach: 'flach (Seite 1)',
  faecher: 'Fächer (3 Exemplare)',
};

/** Faecher, deren Text die KI-Pruefung als Referenz bekommt (Server: REFERENZ_SLOTS). */
export const REFERENZ_SLOTS: readonly ProduktSlot[] = ['ausgabe', 'quickstart'];

export type ProduktQuelle = 'pdf' | 'bild';
export type CoverUrls = { einzel: string } & Partial<Record<'flach' | 'faecher', string>>;

/** Zeiten: Berliner Wandzeit 'YYYY-MM-DD HH:mm:ss' (formatBerlin, nicht umrechnen). */
export interface ProduktMedienVersion {
  id: number;
  plxProduct: string;
  slot: ProduktSlot;
  sourceKind: ProduktQuelle;
  fileName: string | null;
  fileSha256: string | null;
  pageCount: number | null;
  urls: CoverUrls | null;
  textChars: number | null;
  textPagesRead: number | null;
  textTruncated: boolean;
  createdBy: string | null;
  createdAt: string;
}

export interface ProduktSlotStand {
  slot: ProduktSlot;
  cover: ProduktMedienVersion | null;
  text: ProduktMedienVersion | null;
  updatedBy: string | null;
  updatedAt: string | null;
}

export type StatusWert =
  | 'aktuell'
  | 'veraltet'
  | 'neu_waehlen'
  | 'nur_entwurf'
  | 'nicht_lesbar'
  | 'ungebunden';

export interface BindungsStatus {
  roh: string;
  aufloesung: { status: 'ok' | 'variante_fehlt' | 'fach_leer' | 'ungueltig'; url?: string };
  liveSrc: string | null;
  status: StatusWert;
}

export interface StreckenStatus {
  webinarId: string;
  titel: string;
  status: StatusWert;
  seiten: Array<{ pageType: string; status: StatusWert; bindungen: BindungsStatus[] }>;
}

export interface ProduktMedienHinweis {
  webinarId: string;
  plxProduct: string;
  status: 'veraltet' | 'neu_waehlen' | 'nicht_lesbar';
  text: string;
}

export interface ProduktMedienAntwort {
  webinarId: string | null;
  /** Roher Schluessel (adserver_ss_webinars.plx_product, z. B. 'TAP'); null = Webinar ohne Produkt. */
  plxProduct: string | null;
  produktName: string | null;
  /** false = Tabellen fehlen (Migration noch nicht eingespielt). */
  bereit: boolean;
  faecher: ProduktSlotStand[];
  strecken: StreckenStatus[];
  referenzMaxZeichen: number;
}

export interface ApiErgebnis<T> {
  success: boolean;
  data?: T;
  error?: string;
  /** HTTP-Status der Fehlerantwort (409 Konflikt, 503 Migration fehlt …); 0 = keine Antwort. */
  status?: number;
  /** details.code des Servers (z. B. 'konflikt', 'migration_fehlt'). */
  code?: string;
}

async function anfrage<T>(
  method: 'GET' | 'POST',
  url: string,
  body?: FormData | Record<string, unknown>,
): Promise<ApiErgebnis<T>> {
  try {
    const res = await api.request({ url, method, data: body });
    const b = res.data;
    if (b && typeof b === 'object' && b.success === false)
      return { success: false, error: b.error || 'Unbekannter Fehler', status: res.status };
    return { success: true, data: (b?.data ?? b) as T, status: res.status };
  } catch (e: any) {
    const status: number = e?.response?.status ?? 0;
    const payload = e?.response?.data;
    const obj = payload && typeof payload === 'object' ? payload : null;
    return {
      success: false,
      status,
      code: obj?.details?.code,
      error: obj?.error || e?.message || 'Anfrage fehlgeschlagen',
    };
  }
}

const BASE = '/api/webinar-lp/produkt-medien';
const fach = (plxProduct: string, slot: ProduktSlot) =>
  `${BASE}/${encodeURIComponent(plxProduct)}/${slot}`;

export function ladeProduktMedien(webinarId: string) {
  return anfrage<ProduktMedienAntwort>(
    'GET',
    `${BASE}/by-webinar/${encodeURIComponent(webinarId)}`,
  );
}

export function ladeHinweise() {
  return anfrage<{ hinweise: ProduktMedienHinweis[] }>('GET', `${BASE}/hinweise`);
}

export function ladeVersionen(plxProduct: string, slot: ProduktSlot) {
  return anfrage<{ versionen: ProduktMedienVersion[] }>(
    'GET',
    `${fach(plxProduct, slot)}/versions`,
  );
}

/**
 * Erwarteter Zeiger fuer die Konfliktpruefung: Zahl = genau diese Version, null = es darf noch
 * keine geben, undefined = keine Pruefung. Multipart: '' / 'none' / Zahl.
 */
export type Erwartet = number | null | undefined;
const erwartetFeld = (v: Erwartet): string =>
  v === undefined ? '' : v === null ? 'none' : String(v);

export interface UploadEingabe {
  plxProduct: string;
  slot: ProduktSlot;
  quelle: ProduktQuelle;
  /** PDF: gerasterte Seite 1; Bild: das Mockup. */
  bild: Blob | null;
  bildName?: string;
  coverUebernehmen: boolean;
  text?: string | null;
  dateiName?: string | null;
  dateiSha256?: string | null;
  seiten?: number | null;
  textSeitenGelesen?: number | null;
  textGekuerzt?: boolean;
  erwarteteCoverId: Erwartet;
  erwarteteTextId: Erwartet;
}

export interface UploadAntwort {
  version: ProduktMedienVersion;
  fach: ProduktSlotStand;
  strecken: StreckenStatus[];
}

export function ladeHoch(e: UploadEingabe) {
  const f = new FormData();
  if (e.bild) f.append('bild', e.bild, e.bildName || (e.quelle === 'pdf' ? 'seite1.png' : 'bild'));
  f.append('quelle', e.quelle);
  f.append('coverUebernehmen', e.coverUebernehmen ? '1' : '0');
  if (e.quelle === 'pdf' && e.text) f.append('text', e.text);
  if (e.dateiName) f.append('dateiName', e.dateiName);
  if (e.dateiSha256) f.append('dateiSha256', e.dateiSha256);
  if (e.seiten != null) f.append('seiten', String(e.seiten));
  if (e.textSeitenGelesen != null) f.append('textSeitenGelesen', String(e.textSeitenGelesen));
  f.append('textGekuerzt', e.textGekuerzt ? '1' : '0');
  f.append('erwarteteCoverId', erwartetFeld(e.erwarteteCoverId));
  f.append('erwarteteTextId', erwartetFeld(e.erwarteteTextId));
  return anfrage<UploadAntwort>('POST', fach(e.plxProduct, e.slot), f);
}

export function stelleWiederHer(
  plxProduct: string,
  slot: ProduktSlot,
  versionId: number,
  erwartet: { cover: Erwartet; text: Erwartet },
) {
  // JSON: Feld fehlt = keine Pruefung, null = es darf keinen geben, Zahl = genau diese.
  const body: Record<string, unknown> = {};
  if (erwartet.cover !== undefined) body.erwarteteCoverId = erwartet.cover;
  if (erwartet.text !== undefined) body.erwarteteTextId = erwartet.text;
  return anfrage<{ fach: ProduktSlotStand; strecken: StreckenStatus[] }>(
    'POST',
    `${fach(plxProduct, slot)}/restore/${versionId}`,
    body,
  );
}

/** Aufgeloeste Cover-Variante eines Fachs (fuer Vorschau und Bindungsdialog). */
export function coverUrl(stand: ProduktSlotStand | undefined, v: ProduktVariante): string | null {
  const urls = stand?.cover?.urls;
  return (urls && urls[v]) || null;
}
